import { useMemo } from 'react'

import { Collapse, CollapseProps, Spin } from 'antd'
import { createStyles } from 'antd-style'
import { partition } from 'lodash'

import { DataError } from '@eastcoal/common/components/DataError'
import { pick } from '@eastcoal/common/utils/object'

import { TemplateVariableType } from '@eastcoal/shared/enums/TemplateVariableType'

import { useContractDesignerStore } from '../../Store/hooks/useStore'

import { TableVariableButton } from './TableVariableButton'
import { TextVariableButton } from './TextVariableButton'

const useStyles = createStyles(({ token }) => ({
  collapsePanel: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 160px))',
    gap: token.marginXS
  }
}))

const collapseItemStyles = {
  header: {
    paddingInline: 0
  },
  body: {
    paddingInline: 0
  }
}

export const TemplateVariableList = () => {
  const {
    templateVariableData,
    templateVariableError,
    templateVariableLoading,
    refreshTemplateVariableData
  } = useContractDesignerStore(state =>
    pick(state, [
      'templateVariableData',
      'templateVariableLoading',
      'templateVariableError',
      'refreshTemplateVariableData'
    ])
  )

  const { styles } = useStyles()

  const items = useMemo(() => {
    const [textVariables, tableVariables] = partition(
      templateVariableData,
      item => item.type === TemplateVariableType.TEXT
    )
    return [
      {
        key: 'textVariables',
        label: '文本变量',
        styles: collapseItemStyles,
        children: (
          <div className={styles.collapsePanel}>
            {textVariables.map(item => (
              <TextVariableButton key={item.id} data={item} />
            ))}
          </div>
        )
      },
      {
        key: 'tableVariables',
        label: '表格变量',
        styles: collapseItemStyles,
        children: (
          <div className={styles.collapsePanel}>
            {tableVariables.map(item => (
              <TableVariableButton key={item.id} data={item} />
            ))}
          </div>
        )
      }
    ] as Required<CollapseProps>['items']
  }, [styles.collapsePanel, templateVariableData])

  const defaultActiveKey = useMemo(
    () => items.map(item => `${item.key}`),
    [items]
  )

  return templateVariableError ? (
    <DataError
      error={templateVariableError}
      reload={refreshTemplateVariableData}
    />
  ) : (
    <Spin spinning={templateVariableLoading}>
      <Collapse
        ghost
        bordered={false}
        defaultActiveKey={defaultActiveKey}
        items={items}
      />
    </Spin>
  )
}
